import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Trans } from 'react-i18next';
import { find } from 'lodash';
import styled, { css } from 'styled-components';
import colors from 'hsl-shared-components/lib/Theme/colors';
import {
  LAPTOP_BREAKPOINT_IN_PX,
  TABLET_BREAKPOINT_IN_PX,
  MOBILE_BREAKPOINT_IN_PX,
} from '../../constants';
import { setSeasonalTicketZones as setSeasonalTicketZonesAction } from '../../redux/cardReader/actions';

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  margin-bottom: 10px;
`;

const SelectText = styled.div`
  font-size: 20px;
  font-weight: 400;
  letter-spacing: -0.5px;
  line-heigth: 24px;
  color: ${colors.primary.hslGreyDark};
  margin-top: 0;
  margin-bottom: 10px;

  @media screen and (max-width: ${LAPTOP_BREAKPOINT_IN_PX}px) {
    font-size: 18px;
  }

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    font-size: 16px;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    margin-left: 20px;
  }
`;

const optionsByZone = {
  A: [
    { value: 'AB', name: 'AB' },
    { value: 'ABC', name: 'ABC' },
    { value: 'BC', name: 'BC' },
    { value: 'BCD', name: 'BCD' },
    { value: 'CD', name: 'CD' },
    { value: 'D', name: 'D' },
  ],
  B: [
    { value: 'AB', name: 'AB' },
    { value: 'ABC', name: 'ABC' },
    { value: 'BC', name: 'BC' },
    { value: 'BCD', name: 'BCD' },
    { value: 'CD', name: 'CD' },
    { value: 'D', name: 'D' },
  ],
  C: [
    { value: 'AB', name: 'AB' },
    { value: 'ABC', name: 'ABC' },
    { value: 'BC', name: 'BC' },
    { value: 'BCD', name: 'BCD' },
    { value: 'CD', name: 'CD' },
    { value: 'D', name: 'D' },
  ],
  D: [
    { value: 'AB', name: 'AB' },
    { value: 'ABC', name: 'ABC' },
    { value: 'BC', name: 'BC' },
    { value: 'BCD', name: 'BCD' },
    { value: 'CD', name: 'CD' },
    { value: 'D', name: 'D' },
  ],
};

// prettier-ignore
const Zone = styled.a`
  width: fit-content;
  min-width: 25px;
  padding-left: 25px;
  padding-right: 25px;
  height: 45px;
  margin-right: 0.75em;

  background: #27619C;
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  color: ${colors.background.hslGreyXXLight};
  background-color: ${colors.primary.hslBlue};
  cursor: pointer;
  user-select: none;

  @media screen and (max-width: ${LAPTOP_BREAKPOINT_IN_PX}px) {
    height: 40px;
    padding-left: 20px;
    padding-right: 20px;
    border-radius: 20px;
    margin-right: 0.5em;
  }

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    height: 30px;
    padding-left: 15px;
    padding-right: 15px;
    border-radius: 15px;
    margin-right: 0.25em;
  }

  ${(props) => props.selected && css`
    background: ${colors.primary.hslBlueDark};
  `}

  &:hover {
    cursor: pointer;
    border-color: ${colors.primary.hslBlueDark};
  }

  & > span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-55%);

    @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
      transform: translateY(-50%) translateX(-52%);
    }
  }
`;

const ZoneText = styled.div`
  font-weight: 400;
  letter-spacing: -0.02em;
  font-size: 18px;

  @media screen and (max-width: ${LAPTOP_BREAKPOINT_IN_PX}px) {
    font-size: 14px;
  }

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    font-size: 12px;
  }
`;

const TicketZoneWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 10px;
  margin-bottom: 10px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    margin-left: 20px;
  }
`;

const getSelectedValue = (options, seasonalTicketZones) => {
  if (!options) return null;
  if (find(options, (option) => option.value === seasonalTicketZones)) {
    return seasonalTicketZones;
  }
  return null;
};

class SeasonalTicketZonesSelector extends PureComponent {
  changeZone = (zone) => {
    this.props.setSeasonalTicketZones(this.props.ticketType, zone);
  };

  render() {
    const { currentZone, seasonalTicketZones } = this.props;
    const options = optionsByZone[currentZone[0]];
    const selectedValue = getSelectedValue(options, seasonalTicketZones);
    return (
      <SelectWrapper>
        <SelectText>
          <Trans i18nKey="Generic.seasonalTicketZones" />
        </SelectText>
        <TicketZoneWrapper>
          {options.map((option) => (
            <Zone
              key={option.name}
              selected={selectedValue === option.value}
              onClick={() => this.changeZone(option.value)}
            >
              <ZoneText>{option.name}</ZoneText>
            </Zone>
          ))}
        </TicketZoneWrapper>
      </SelectWrapper>
    );
  }
}

SeasonalTicketZonesSelector.propTypes = {
  ticketType: PropTypes.string.isRequired,
  setSeasonalTicketZones: PropTypes.func.isRequired,
  seasonalTicketZones: PropTypes.string.isRequired,
  currentZone: PropTypes.string.isRequired,
};

const mapStateToProps = ({ cardReader }, ownProps) => ({
  seasonalTicketZones: cardReader[ownProps.ticketType].seasonalTicketZones,
  currentZone: cardReader.currentZone,
});

const mapDispatchToProps = (dispatch) => ({
  setSeasonalTicketZones: (ticketType, zones) => dispatch(setSeasonalTicketZonesAction(ticketType, zones)), // eslint-disable-line
});

// prettier-ignore
export default connect(mapStateToProps, mapDispatchToProps)(SeasonalTicketZonesSelector);
